import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductCard = ({ product, onFavoriteChange }) => {
  const { isAuthenticated } = useAuth();
  const [isFavorited, setIsFavorited] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const toggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error('Faça login para adicionar aos favoritos');
      return;
    }

    try {
      const response = await axios.post(`${API}/favorites/${product.id}`);
      setIsFavorited(response.data.favorited);
      toast.success(response.data.favorited ? 'Adicionado aos favoritos' : 'Removido dos favoritos');
      if (onFavoriteChange) onFavoriteChange();
    } catch (error) {
      toast.error('Erro ao atualizar favoritos');
    }
  };

  return (
    <Link 
      to={`/product/${product.id}`} 
      className="card-product group"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image Container */}
      <div className="relative aspect-[3/4] overflow-hidden bg-card">
        {!imageLoaded && (
          <div className="absolute inset-0 skeleton" />
        )}
        <img
          src={product.images[0]}
          alt={product.name}
          className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
        />
        
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        
        {/* Favorite Button */}
        <button
          onClick={toggleFavorite}
          className="absolute top-4 right-4 p-2 bg-background/80 backdrop-blur-sm hover:bg-primary hover:text-primary-foreground transition-all duration-300 opacity-0 group-hover:opacity-100"
          data-testid={`favorite-btn-${product.id}`}
        >
          <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
        </button>

        {/* Featured Badge */}
        {product.featured && (
          <span className="absolute top-4 left-4 px-3 py-1 bg-primary text-primary-foreground text-xs uppercase tracking-wider">
            Destaque
          </span>
        )}

        {/* Quick View - Bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
          <span className="block w-full py-3 bg-background/90 backdrop-blur-sm text-center text-xs uppercase tracking-[0.2em] hover:bg-primary hover:text-primary-foreground transition-colors">
            Ver Detalhes
          </span>
        </div>
      </div>

      {/* Product Info */}
      <div className="pt-4 space-y-2">
        <h3 className="text-sm tracking-wide group-hover:text-primary transition-colors">
          {product.name}
        </h3>
        <p className="text-primary font-bold">
          ${product.price.toFixed(2)}
        </p>
        <div className="flex gap-2">
          {product.colors.slice(0, 3).map((color, idx) => (
            <span 
              key={idx} 
              className="text-xs text-muted-foreground"
            >
              {color}{idx < Math.min(product.colors.length, 3) - 1 ? ',' : ''}
            </span>
          ))}
          {product.colors.length > 3 && (
            <span className="text-xs text-muted-foreground">+{product.colors.length - 3}</span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
